// src/helpers/handlebars_validation.js
import { white_list_outgoing_person_fields } from './static_vars';

/**
 * Checks if a value contains unparsed handlebars syntax
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value contains unparsed handlebars
 */
export function contains_unparsed_handlebars(value) {
	if (typeof value !== 'string') {
		return false;
	}
	
	// Check for handlebars syntax patterns: {{field_name}}
	// This regex matches {{ followed by word characters, underscores, or hyphens, followed by }}
    try {
        const handlebars_pattern = /\{\{[a-zA-Z_][a-zA-Z0-9_-]*\}\}/;
        return handlebars_pattern.test(value);
    } catch (error) {
        console.error(`Error checking for unparsed handlebars: ${error}`);
        return false;
    }
}

/**
 * Validates user data object and removes any fields with unparsed handlebars
 * @param {Object} user_data - The user data object to validate
 * @param {Array} whitelisted_fields - Array of whitelisted field names to check
 * @param {Function} error_function - Optional error function to log issues
 * @returns {Object} - Cleaned user data object with handlebars fields removed
 */
export function validate_and_clean_handlebars_from_user_data(
	user_data = {},
	whitelisted_fields = white_list_outgoing_person_fields,
	error_function = null
) {
	if (!user_data || typeof user_data !== 'object') {
		return user_data;
	}

	// Check if whitelisted_fields is a valid array
	if (!Array.isArray(whitelisted_fields) || whitelisted_fields.length === 0) {
		console.warn('whitelisted_fields is not a valid array or is empty, using default');
		whitelisted_fields = white_list_outgoing_person_fields;
	}

	const cleaned_user_data = {};
	const removed_fields = [];

	try {
		Object.keys(user_data).forEach(key => {
		const value = user_data[key];
		
		// Only check whitelisted fields for handlebars
		if (whitelisted_fields.includes(key)) {
			if (contains_unparsed_handlebars(value)) {
				removed_fields.push(key);
				console.warn(`Removed unparsed handlebars from field '${key}': ${value}`);
			} else {
				cleaned_user_data[key] = value;
			}
		} else {
			// For non-whitelisted fields, keep them as-is
			cleaned_user_data[key] = value;
		}
	});
	} catch (error) {
		console.error(`Error validating user data for handlebars: ${error}`);
		return user_data; // Return original data if validation fails
	}

	return cleaned_user_data;
}

/**
 * Validates a single field value for unparsed handlebars
 * @param {string} key - The field key
 * @param {any} value - The field value
 * @param {Array} whitelisted_fields - Array of whitelisted field names to check
 * @param {Function} error_function - Optional error function to log issues
 * @returns {any} - The value if valid, null if contains handlebars
 */
export function validate_single_field_for_handlebars(
	key,
	value,
	whitelisted_fields = white_list_outgoing_person_fields,
	error_function = null
) {
	// Check if whitelisted_fields is a valid array
	if (!Array.isArray(whitelisted_fields) || whitelisted_fields.length === 0) {
		console.warn('whitelisted_fields is not a valid array or is empty, using default');
		whitelisted_fields = white_list_outgoing_person_fields;
	}

	// Only check whitelisted fields
	if (!whitelisted_fields.includes(key)) {
		return value;
	}

	try {
		if (contains_unparsed_handlebars(value)) {
			console.warn(`Skipping field '${key}' with unparsed handlebars: ${value}`);
			return null;
		}
	} catch (error) {
		console.error(`Error validating field '${key}' for handlebars: ${error}`);
		return value; // Return original value if validation fails
	}

	return value;
}
